/**
 * The one place a 401 is noticed, and the seam that lets there be only one.
 *
 * A session can end between any two requests: the operator revoked it from another browser,
 * the 12-hour idle window elapsed, the 7-day absolute one did, or someone hit "revoke
 * everything" during an incident (`AuthN-AuthZ#sessions` — all four are real and all four
 * are server-side). Every screen therefore has to cope with a request that was fine a
 * minute ago answering 401.
 *
 * **Fourteen screens each handling that is fourteen chances to forget one**, and the one
 * forgotten is the screen that then sits there showing data from a session that no longer
 * exists. So the handling is attached to the query client's caches in `main.tsx` — every
 * query and every mutation passes through them, including ones written later by someone who
 * has never read this file.
 *
 * The indirection exists because of construction order: the query client is built at module
 * scope, before React mounts, and the thing that must be told is `AuthProvider`'s state,
 * which does not exist yet. A module-level slot is the smallest thing that bridges that —
 * smaller than a custom DOM event, and unlike one it is typed and has exactly one listener
 * by construction.
 */

type Listener = () => void;

let listener: Listener | null = null;

/** Registered by `AuthProvider` on mount. Returns the unsubscribe. */
export function onSessionLost(next: Listener): () => void {
  listener = next;
  return () => {
    if (listener === next) listener = null;
  };
}

/**
 * Called from the query client's cache handlers when a request answers 401.
 *
 * Silent when nothing is registered — that is the window before React mounts, where no
 * request has been made yet and there is nothing to forget.
 */
export function sessionLost(): void {
  listener?.();
}
